/**
 * SketchCanvas — トキワ荘の最小描画キャンバス（TKW-01/02）
 * ツールは最小（ペン/消しゴム/太さ/色/取り消し/クリア）。手ブレ補正・レイヤーはβ対象外（TOKIWA_SPEC §5.5）。
 * 確定した一枚は onExport(blob) でPNGを返す（呼び出し元がサーバ保存＋手元DL）。
 * 2026-08-14: 色パレット・筆5段を追加（共有キャンバスと同じ BrushControls を共用）。
 */
import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { BrushControls, BrushTool, BRUSH_COLORS } from './BrushControls';

export type SketchCanvasHandle = { exportPng: () => Promise<Blob | null>; isBlank: () => boolean };

export const SketchCanvas = forwardRef<SketchCanvasHandle, { width?: number; height?: number }>(
  ({ width = 640, height = 420 }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawing = useRef(false);
    const dirty = useRef(false);
    const undoStack = useRef<ImageData[]>([]);
    const [tool, setTool] = useState<BrushTool>('pen');
    const [size, setSize] = useState(4);
    const [color, setColor] = useState<string>(BRUSH_COLORS[0]);

    useEffect(() => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, c.width, c.height);
    }, []);

    useImperativeHandle(ref, () => ({
      exportPng: () =>
        new Promise((resolve) => {
          const c = canvasRef.current;
          if (!c) return resolve(null);
          c.toBlob((b) => resolve(b), 'image/png');
        }),
      isBlank: () => !dirty.current,
    }));

    const pos = (e: React.PointerEvent) => {
      const c = canvasRef.current!;
      const rect = c.getBoundingClientRect();
      return { x: (e.clientX - rect.left) * (c.width / rect.width), y: (e.clientY - rect.top) * (c.height / rect.height) };
    };

    const start = (e: React.PointerEvent) => {
      const c = canvasRef.current, ctx = c?.getContext('2d');
      if (!c || !ctx) return;
      undoStack.current.push(ctx.getImageData(0, 0, c.width, c.height));
      if (undoStack.current.length > 30) undoStack.current.shift();
      drawing.current = true;
      dirty.current = true;
      const p = pos(e);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      c.setPointerCapture(e.pointerId);
    };
    const move = (e: React.PointerEvent) => {
      if (!drawing.current) return;
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      const p = pos(e);
      ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
      ctx.lineWidth = tool === 'eraser' ? size * 3 : size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    };
    const end = () => { drawing.current = false; };

    const undo = () => {
      const c = canvasRef.current, ctx = c?.getContext('2d');
      if (!c || !ctx) return;
      const prev = undoStack.current.pop();
      if (prev) ctx.putImageData(prev, 0, 0);
    };
    const clear = () => {
      const c = canvasRef.current, ctx = c?.getContext('2d');
      if (!c || !ctx) return;
      undoStack.current.push(ctx.getImageData(0, 0, c.width, c.height));
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, c.width, c.height);
    };

    return (
      <div>
        <BrushControls
          tool={tool} color={color} width={size}
          onTool={setTool} onColor={setColor} onWidth={setSize}
          trailing={
            <>
              <button onClick={undo} className="btn btn-ghost btn-sm min-h-[44px]">取り消し</button>
              <button onClick={clear} className="btn btn-ghost btn-sm min-h-[44px]">クリア</button>
            </>
          }
        />
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          className="w-full rounded-pop-sm border-2 border-ink bg-white touch-none"
          style={{ aspectRatio: `${width} / ${height}`, cursor: 'crosshair' }}
        />
      </div>
    );
  }
);
